import dayjs from 'dayjs';

/**
 * Utilities for handling TypeORM `type: 'date'` columns (calendar dates with no
 * meaningful time-of-day - e.g. Right.termFromDate, Commercial.last_shoot_date,
 * Session.date). These always round-trip through the API as a full ISO datetime
 * string at UTC midnight (e.g. "2019-07-01T00:00:00.000Z").
 *
 * Never format one of these values with `dayjs(value).format(...)` or
 * `new Date(value).toLocaleDateString()` directly - both convert to the viewer's
 * local timezone first, which rolls the calendar day back by one for any viewer
 * west of UTC (confirmed live: Right term dates displayed a day early). Route
 * every date-only value through these helpers instead; `toDateOnly` is pure
 * string slicing, so it's immune to the viewer's timezone.
 */

// Extracts the "YYYY-MM-DD" calendar date, stripping any time-of-day/timezone
// component. Safe to call on a bare "YYYY-MM-DD" string too (a no-op).
export const toDateOnly = (value?: string | Date | null): string => {
  if (!value) return '';
  const str = value instanceof Date ? value.toISOString() : value;
  return str.slice(0, 10);
};

// Formats a date-only value for display. Parses the pre-sliced "YYYY-MM-DD"
// string, which dayjs (unlike a full "...Z" string) parses as local midnight -
// safe because both the parse and the format happen in the same local frame.
export const formatDateOnly = (value?: string | Date | null, format = 'MM/DD/YYYY'): string => {
  const dateOnly = toDateOnly(value);
  return dateOnly ? dayjs(dateOnly).format(format) : '';
};

// Normalizes whatever Grommet's DateInput onChange hands back (a single ISO
// string, or a [start, end] array in range mode) into a plain date-only string.
export const fromDateInputChange = (value: unknown): string => {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === 'string' ? toDateOnly(raw) : '';
};
